import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { LocaleLink } from "@/components/LocaleLink";
import { useT } from "@/lib/i18n";
import { COLOR_CATEGORIES } from "@/lib/colors";
import { shopifyImg, shopifySrcSet } from "@/lib/shopify-image";

/**
 * "Shop efter farve" — eight image-led tiles with redaktionel asymmetri.
 *
 * Each tile drifts gently with scroll (alternating directions) so the
 * grid feels alive instead of like a static thumbnail wall. Tiles use a
 * staggered aspect to break the rigid square grid.
 */
export function ColorShopStrip() {
  const { t, locale } = useT();
  const reduce = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  // Two parallax tracks — odd tiles drift up, even tiles drift down
  const yUp = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["8%", "-8%"]);
  const yDown = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["-6%", "6%"]);

  return (
    <section
      ref={sectionRef}
      data-tour="colors"
      className="relative mx-auto max-w-[1500px] px-6 pb-20 pt-16 md:px-10 md:pb-24 md:pt-20"
    >
      {/* Headline — magazine layout: eyebrow above, big italic centred-left */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="mb-10 md:mb-14"
      >
        <div className="flex items-center gap-3">
          <span className="stripe-rainbow block h-1.5 w-14 rounded-full" aria-hidden />
          <p className="eyebrow">{t("home.colorshop.eyebrow")}</p>
        </div>
        <h2 className="mt-4 font-display text-3xl leading-[1.02] md:text-5xl">
          {t("home.colorshop.titleA")}{" "}
          <span className="display-italic text-terracotta">
            {t("home.colorshop.titleB")}
          </span>
        </h2>
      </motion.div>

      <div className="-mx-6 flex snap-x snap-mandatory gap-3 overflow-x-auto px-6 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:grid sm:grid-cols-4 sm:overflow-visible sm:px-0 sm:pb-0 md:gap-4 lg:grid-cols-8">
        {COLOR_CATEGORIES.map((c, i) => {
          return (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="w-[40vw] max-w-[180px] shrink-0 snap-start sm:w-auto sm:max-w-none"
            >
              <LocaleLink
                to="/farver/$slug"
                params={{ slug: c.slug }}
                className={`group relative block aspect-square overflow-hidden rounded-sm transition-all duration-700 ease-out hover:-translate-y-1 hover:shadow-[0_30px_60px_-20px_oklch(0.24_0.025_285/0.20)] ${c.panel}`}
                aria-label={locale === "da" ? c.labelDa : c.labelEn}
              >
                {c.image ? (
                  <img
                    src={shopifyImg(c.image, { width: 600 })}
                    srcSet={shopifySrcSet(c.image, [320, 480, 640, 800])}
                    sizes="(min-width: 768px) 25vw, 50vw"
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.10]"
                  />
                ) : null}

                {/* Bottom gradient for label legibility */}
                <div
                  aria-hidden
                  className="absolute inset-0 transition-opacity duration-700 group-hover:opacity-90"
                  style={{
                    background: c.image
                      ? "linear-gradient(180deg, transparent 40%, oklch(0.14 0.01 50 / 0.65) 100%)"
                      : "linear-gradient(180deg, transparent 60%, oklch(0.14 0.01 50 / 0.20) 100%)",
                  }}
                />

                <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 p-2.5 md:p-3">
                  <span
                    className="block h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-white/70"
                    style={{ backgroundColor: c.swatch }}
                    aria-hidden
                  />
                  <h3
                    className={`font-display text-sm leading-tight md:text-base ${
                      c.image ? "text-background" : "text-foreground"
                    }`}
                  >
                    {locale === "da" ? c.labelDa : c.labelEn}
                  </h3>
                </div>
              </LocaleLink>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
